// §4.2 Demand pool (`/planning/demand`): the entries of the facility's latest
// succeeded refresh with their labels, date-basis chips and per product and
// week subtotals, the filters, and the consumption drill-in on a forecast row.
//
// D-560 ruling 5 (P3-UX leftovers): one table at a time. The entries and the
// weekly totals were two 50-row tables on one page, 4,023 px tall at 1440
// (4.5 screens) and, under six stacked filters, the first row sat below the
// first screen of a 412 px phone. The filters now sit in the rev-7 FilterBar
// (product and source in the row, the rest behind Filters, all of them in
// the sheet on a phone), a tab strip switches between the entries and the
// totals by week, each pages at 50, and the refresh line and the drill hint
// follow the table they describe.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Tabs } from "../../../../components/tabs";
import { Banner } from "../../../../components/toast";
import { formatBusinessDate, formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { addDaysIso, formatGridQty } from "../../../../components/bucket-grid-paste";
import {
  DateBasisChip,
  OrderLabel,
  QtyWithUnit,
  SOURCE_VARIANT,
  TransferLabel,
  TriggerChip,
  useDemandRefreshControl,
} from "../components/demand-bits";
import {
  DEMAND_DATE_BASES,
  DEMAND_SOURCE_KINDS,
  useDemandEntries,
  useForecastConsumption,
  type DemandDateBasis,
  type DemandEntry,
  type DemandEntryFilters,
  type DemandRefresh,
  type DemandSourceKind,
  type DemandSubtotal,
} from "../hooks/demand-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { problemCode, useProductIndex, useUoms, type Product } from "../hooks/planning-queries";
import { weekLabel } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { shortId } from "../../../../lib/short-id";

/** Subtotal rows shown per page; the server serves the whole filtered set. */
const SUBTOTAL_PAGE = 50;

const PURGED = new Set(["DEMAND_REFRESH_PURGED", "DEMAND_REFRESH_NOT_SUCCEEDED"]);

/** The Mondays of a refresh's horizon, the bucket filter's choices. */
export function horizonWeeks(refresh: DemandRefresh): string[] {
  return Array.from({ length: refresh.horizon_weeks }, (_, i) =>
    addDaysIso(refresh.horizon_start, 7 * i),
  );
}

interface DrillIn {
  readonly forecastId: string;
  readonly productId: string;
  readonly bucket: string;
  readonly refreshId: string;
}

export function PlanningDemandScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canRefresh = perms.has("planning.demand.manage");
  const control = useDemandRefreshControl(facility.id, facility.timezone, { canRefresh });

  const [productId, setProductId] = useState("");
  const [sourceKind, setSourceKind] = useState<DemandSourceKind | "">("");
  const [basis, setBasis] = useState<DemandDateBasis | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [plannerText, setPlannerText] = useState("");
  const plannerCode = useDebouncedValue(plannerText.trim(), 400);
  const filters: DemandEntryFilters = useMemo(
    () => ({
      product_id: productId || undefined,
      source_kind: sourceKind || undefined,
      date_basis: basis || undefined,
      bucket_from: from || undefined,
      bucket_to: to || undefined,
      planner_code: plannerCode || undefined,
    }),
    [productId, sourceKind, basis, from, to, plannerCode],
  );
  const filtered = Object.values(filters).some((v) => v !== undefined);

  // Page 1 reads the latest refresh; later pages name it, so one pool is paged.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pinned, setPinned] = useState<string | undefined>(undefined);
  const [replaced, setReplaced] = useState(false);
  const page = cursors.length - 1;
  const entries = useDemandEntries(
    facility.id,
    page > 0 ? pinned : undefined,
    filters,
    cursors[page],
  );
  const [subPage, setSubPage] = useState(0);
  const [drill, setDrill] = useState<DrillIn | undefined>(undefined);
  const [view, setView] = useState<"entries" | "totals">("entries");

  useEffect(() => {
    setCursors([undefined]);
    setSubPage(0);
  }, [facility.id, filters]);
  useEffect(() => {
    setProductId("");
    setPinned(undefined);
    setReplaced(false);
  }, [facility.id]);

  const shown = entries.data?.refresh ?? null;
  useEffect(() => {
    if (page === 0 && shown !== null && shown.id !== pinned) setPinned(shown.id);
  }, [page, shown, pinned]);

  const purged = PURGED.has(problemCode(entries.error) ?? "");
  useEffect(() => {
    if (!purged) return;
    setReplaced(true);
    setCursors([undefined]);
  }, [purged]);

  useEffect(() => {
    if (entries.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [entries.error, navigate]);

  const items = entries.data?.items ?? [];
  const subtotals = entries.data?.subtotals ?? [];
  const productIds = [
    ...items.map((e) => e.product_id),
    ...subtotals.map((s) => s.product_id),
    ...(productId ? [productId] : []),
  ];
  const products = useProductIndex(productIds);
  const uoms = useUoms();
  const unitOf = (id: string): string | undefined => {
    const product = products.byId.get(id);
    if (product === undefined) return undefined;
    return uoms.data?.items.find((u) => u.id === product.base_uom_id)?.code;
  };
  const productChoices = useMemo(
    () => Array.from(new Set(subtotals.map((s) => s.product_id))),
    [subtotals],
  );

  const time = (iso: string | null) => formatTimestamp(iso, i18n.language, facility.timezone);
  const date = (iso: string) => formatBusinessDate(iso, i18n.language);

  const clearFilters = () => {
    setProductId("");
    setSourceKind("");
    setBasis("");
    setFrom("");
    setTo("");
    setPlannerText("");
  };

  const pageHead = <PlanningHeader title={t("planning.demand.title")} actions={control.button} />;

  if (entries.error !== null && !purged && !(entries.error instanceof UnauthorizedError)) {
    return (
      <section className="planning-screen" data-testid="planning-demand">
        {pageHead}
        <ErrorState
          title={t("planning.demand.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void entries.refetch()}
          requestId={requestIdOf(entries.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  if (entries.isPending || entries.data === undefined) {
    return (
      <section className="planning-screen" data-testid="planning-demand">
        {pageHead}
        <CardSkeleton lines={8} label={t("planning.demand.loading")} />
      </section>
    );
  }

  if (shown === null) {
    return (
      <section className="planning-screen" data-testid="planning-demand">
        {pageHead}
        {control.banner}
        <EmptyState
          title={t("planning.demand.noRefreshTitle")}
          body={t("planning.demand.noRefreshBody")}
          ctaLabel={canRefresh && !control.running ? t("planning.demand.refresh") : undefined}
          onCta={canRefresh && !control.running ? control.start : undefined}
        />
      </section>
    );
  }

  const weeks = horizonWeeks(shown);
  const qtyCell = (qty: string, id: string) => <QtyWithUnit qty={qty} unit={unitOf(id)} />;
  const productCell = (id: string) => <ProductName product={products.byId.get(id)} id={id} />;

  const entryColumns: DataTableColumn<DemandEntry>[] = [
    {
      key: "product",
      header: t("planning.demand.columns.product"),
      id: true,
      render: (e) => productCell(e.product_id),
    },
    {
      key: "source",
      header: t("planning.demand.columns.source"),
      render: (e) => (
        <span className="planning-source">
          <StatusChip
            variant={SOURCE_VARIANT[e.source_kind]}
            dot={false}
            data-status={e.source_kind}
          >
            {t(`planning.demand.source.${e.source_kind}`)}
          </StatusChip>
          {e.source_kind === "sales_order" ? (
            <OrderLabel label={e.label} testId="planning-demand-label" />
          ) : null}
          {e.source_kind === "transfer" ? (
            <TransferLabel
              transfer={e.transfer}
              orderId={e.transfer_planned_order_id}
              testId="planning-demand-transfer"
            />
          ) : null}
        </span>
      ),
    },
    {
      key: "basis",
      header: t("planning.demand.columns.basis"),
      render: (e) => (e.date_basis !== null ? <DateBasisChip basis={e.date_basis} /> : null),
    },
    {
      key: "bucket",
      header: t("planning.demand.columns.bucket"),
      render: (e) => weekLabel(e.bucket_start, i18n.language),
    },
    { key: "due", header: t("planning.demand.columns.due"), render: (e) => date(e.due_date) },
    {
      key: "original",
      header: t("planning.demand.columns.original"),
      numeric: true,
      render: (e) => qtyCell(e.qty_original, e.product_id),
    },
    {
      key: "consumed",
      header: t("planning.demand.columns.consumed"),
      numeric: true,
      render: (e) => (e.source_kind === "forecast" ? qtyCell(e.qty_consumed, e.product_id) : null),
    },
    {
      key: "net",
      header: t("planning.demand.columns.net"),
      numeric: true,
      render: (e) => (
        <QtyWithUnit qty={e.qty_net} unit={unitOf(e.product_id)} testId="planning-demand-net" />
      ),
    },
  ];

  const subtotalColumns: DataTableColumn<DemandSubtotal>[] = [
    {
      key: "product",
      header: t("planning.demand.columns.product"),
      id: true,
      render: (s) => productCell(s.product_id),
    },
    {
      key: "bucket",
      header: t("planning.demand.columns.bucket"),
      render: (s) => weekLabel(s.bucket_start, i18n.language),
    },
    {
      key: "orders",
      header: t("planning.demand.source.sales_order"),
      numeric: true,
      render: (s) => qtyCell(s.sales_order, s.product_id),
    },
    {
      key: "forecast",
      header: t("planning.demand.source.forecast"),
      numeric: true,
      render: (s) => qtyCell(s.forecast, s.product_id),
    },
    {
      key: "safety",
      header: t("planning.demand.source.safety_stock"),
      numeric: true,
      render: (s) => qtyCell(s.safety_stock, s.product_id),
    },
    {
      key: "total",
      header: t("planning.demand.columns.total"),
      numeric: true,
      render: (s) => (
        <QtyWithUnit qty={s.total} unit={unitOf(s.product_id)} testId="planning-subtotal-total" />
      ),
    },
  ];
  const subRows = subtotals.slice(subPage * SUBTOTAL_PAGE, (subPage + 1) * SUBTOTAL_PAGE);
  const subPages = Math.max(1, Math.ceil(subtotals.length / SUBTOTAL_PAGE));

  const productName = productId ? (products.byId.get(productId)?.sku ?? shortId(productId)) : "";
  const weekName = (w: string) => weekLabel(w, i18n.language);
  const chipOf = (key: string, filter: string, value: string, clear: () => void): FilterChip => ({
    key,
    label: t("planning.demand.filterChip", { filter, value }),
    onRemove: clear,
  });
  const chips: FilterChip[] = [
    productId
      ? chipOf("product", t("planning.demand.filters.product"), productName, () => {
          setProductId("");
        })
      : undefined,
    sourceKind
      ? chipOf(
          "source",
          t("planning.demand.filters.source"),
          t(`planning.demand.source.${sourceKind}`),
          () => {
            setSourceKind("");
          },
        )
      : undefined,
    basis
      ? chipOf(
          "basis",
          t("planning.demand.filters.basis"),
          t(`planning.demand.basis.${basis}`),
          () => {
            setBasis("");
          },
        )
      : undefined,
    from
      ? chipOf("from", t("planning.demand.filters.from"), weekName(from), () => {
          setFrom("");
        })
      : undefined,
    to
      ? chipOf("to", t("planning.demand.filters.to"), weekName(to), () => {
          setTo("");
        })
      : undefined,
    plannerCode
      ? chipOf("planner", t("planning.demand.filters.plannerCode"), plannerCode, () => {
          setPlannerText("");
        })
      : undefined,
  ].filter((c): c is FilterChip => c !== undefined);
  // The button counts what sits behind it; on a phone every filter does.
  const behindButton = [basis, from, to, plannerCode].filter((v) => v !== "").length;

  const filterNames = chips.map((c) => c.label);

  const empty = filtered ? (
    <EmptyState
      title={t("planning.demand.filteredEmptyTitle")}
      body={t("planning.demand.filteredEmptyBody", { filters: filterNames.join(", ") })}
      ctaLabel={t("planning.common.clearFilters")}
      onCta={clearFilters}
    />
  ) : (
    <EmptyState title={t("planning.demand.emptyTitle")} body={t("planning.demand.emptyBody")} />
  );

  const panelId = "planning-demand-panel";

  return (
    <section className="planning-screen" data-testid="planning-demand">
      {pageHead}
      {control.banner}
      {replaced ? (
        <Banner kind="warn" data-testid="planning-demand-replaced">
          {t("planning.demand.replaced")}
        </Banner>
      ) : null}
      <FilterBar
        data-testid="planning-demand-filters"
        filters={{
          inline: (
            <>
              <Field label={t("planning.demand.filters.product")}>
                <Select
                  value={productId}
                  data-testid="planning-demand-filter-product"
                  onChange={(e) => {
                    setProductId(e.target.value);
                  }}
                >
                  <option value="">{t("planning.common.all")}</option>
                  {productChoices.map((id) => (
                    <option key={id} value={id}>
                      {products.byId.get(id)?.sku ?? shortId(id)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("planning.demand.filters.source")}>
                <Select
                  value={sourceKind}
                  data-testid="planning-demand-filter-source"
                  onChange={(e) => {
                    setSourceKind(e.target.value as DemandSourceKind | "");
                  }}
                >
                  <option value="">{t("planning.common.all")}</option>
                  {DEMAND_SOURCE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`planning.demand.source.${k}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          ),
          more: (
            <>
              <Field label={t("planning.demand.filters.basis")}>
                <Select
                  value={basis}
                  data-testid="planning-demand-filter-basis"
                  onChange={(e) => {
                    setBasis(e.target.value as DemandDateBasis | "");
                  }}
                >
                  <option value="">{t("planning.common.all")}</option>
                  {DEMAND_DATE_BASES.map((b) => (
                    <option key={b} value={b}>
                      {t(`planning.demand.basis.${b}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("planning.demand.filters.from")}>
                <Select
                  value={from}
                  data-testid="planning-demand-filter-from"
                  onChange={(e) => {
                    setFrom(e.target.value);
                  }}
                >
                  <option value="">{t("planning.common.all")}</option>
                  {weeks.map((w) => (
                    <option key={w} value={w}>
                      {weekName(w)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("planning.demand.filters.to")}>
                <Select
                  value={to}
                  data-testid="planning-demand-filter-to"
                  onChange={(e) => {
                    setTo(e.target.value);
                  }}
                >
                  <option value="">{t("planning.common.all")}</option>
                  {weeks.map((w) => (
                    <option key={w} value={w}>
                      {weekName(w)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("planning.demand.filters.plannerCode")}>
                <input
                  type="search"
                  value={plannerText}
                  maxLength={64}
                  data-testid="planning-demand-filter-planner"
                  onChange={(e) => {
                    setPlannerText(e.target.value);
                  }}
                />
              </Field>
            </>
          ),
          label: t("planning.common.filtersCount", { count: behindButton }),
          title: t("planning.common.filters"),
          doneLabel: t("planning.common.done"),
        }}
        chips={chips}
        removeLabel={(chip) => t("planning.common.removeFilter", { filter: chip.label })}
        clearAll={{ label: t("planning.common.clearFilters"), onClear: clearFilters }}
      />
      <Tabs
        label={t("planning.demand.views.label")}
        active={view}
        onSelect={(id) => {
          setView(id === "totals" ? "totals" : "entries");
        }}
        controls={panelId}
        data-testid="planning-demand-views"
        tabs={[
          {
            id: "entries",
            label: t("planning.demand.views.entries"),
            "data-testid": "planning-demand-view-entries",
          },
          {
            id: "totals",
            label: t("planning.demand.views.totals"),
            "data-testid": "planning-demand-view-totals",
          },
        ]}
      />
      <div id={panelId} role="tabpanel">
        {view === "totals" ? (
          <Card data-testid="planning-demand-subtotals">
            <DataTable
              columns={subtotalColumns}
              rows={subRows}
              rowKey={(s) => `${s.product_id}|${s.bucket_start}`}
              rowTestId="planning-subtotal-row"
              caption={t("planning.demand.subtotalsTitle")}
              rowProps={(s) => ({ "data-product-id": s.product_id, "data-week": s.bucket_start })}
              empty={empty}
              pager={
                subtotals.length > SUBTOTAL_PAGE
                  ? {
                      range: t("planning.common.page", { page: subPage + 1 }),
                      page: subPage + 1,
                      pageCount: subPages,
                      onPage: (n) => {
                        setSubPage(Math.min(Math.max(0, n - 1), subPages - 1));
                      },
                      prevLabel: t("planning.common.previous"),
                      nextLabel: t("planning.common.next"),
                      hasPrev: subPage > 0,
                      hasNext: subPage + 1 < subPages,
                    }
                  : undefined
              }
            />
          </Card>
        ) : (
          <Card data-testid="planning-demand-entries">
            <DataTable
              columns={entryColumns}
              rows={items}
              rowKey={(e) => e.id}
              rowTestId="planning-demand-row"
              caption={t("planning.demand.entriesTitle")}
              rowProps={(e) => ({
                "data-source": e.source_kind,
                "data-product-id": e.product_id,
                "data-week": e.bucket_start,
                "data-line-id": e.sales_order_line_id ?? undefined,
              })}
              onRowClick={(e) => {
                if (e.source_kind === "forecast" && e.forecast_id !== null) {
                  setDrill({
                    forecastId: e.forecast_id,
                    productId: e.product_id,
                    bucket: e.bucket_start,
                    refreshId: shown.id,
                  });
                }
              }}
              empty={empty}
              pager={{
                range: t("planning.common.page", { page: page + 1 }),
                page: page + 1,
                onPage: (n) => {
                  const next = entries.data.next_cursor;
                  if (n > page + 1 && next !== null) setCursors([...cursors, next]);
                  else if (n < page + 1) setCursors(cursors.slice(0, -1));
                },
                prevLabel: t("planning.common.previous"),
                nextLabel: t("planning.common.next"),
                hasPrev: page > 0,
                hasNext: entries.data.next_cursor !== null,
              }}
            />
            {items.some((e) => e.source_kind === "forecast") ? (
              <Card.Body>
                <p className="planning-hint" data-testid="planning-demand-drill-hint">
                  {t("planning.demand.drillHint")}
                </p>
              </Card.Body>
            ) : null}
          </Card>
        )}
      </div>
      <p className="planning-last" data-testid="planning-demand-shown" data-refresh-id={shown.id}>
        <span>
          {t("planning.demand.shown", { time: time(shown.finished_at ?? shown.started_at) })}
        </span>
        <TriggerChip trigger={shown.trigger} />
      </p>
      {drill !== undefined ? (
        <ConsumptionDialog
          drill={drill}
          product={products.byId.get(drill.productId)}
          unit={unitOf(drill.productId)}
          onClose={() => {
            setDrill(undefined);
          }}
          onPurged={() => {
            setDrill(undefined);
            setReplaced(true);
            setCursors([undefined]);
            void entries.refetch();
          }}
        />
      ) : null}
    </section>
  );
}

function ProductName({ product, id }: { product: Product | undefined; id: string }) {
  if (product === undefined) return <code className="planning-sku">{shortId(id)}</code>;
  return (
    <span className="planning-product">
      <span className="planning-sku">{product.sku}</span>
      <span className="planning-product-name">{product.name}</span>
    </span>
  );
}

function ConsumptionDialog(props: {
  drill: DrillIn;
  product: Product | undefined;
  unit: string | undefined;
  onClose: () => void;
  onPurged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { drill, onPurged } = props;
  const read = useForecastConsumption(drill.forecastId, drill.refreshId);
  const purged = PURGED.has(problemCode(read.error) ?? "");
  useEffect(() => {
    if (purged) onPurged();
  }, [purged, onPurged]);
  const withUnit = (qty: string) =>
    `${formatGridQty(qty, i18n.language)}${props.unit !== undefined ? ` ${props.unit}` : ""}`;
  const cell = read.data?.cells.find(
    (c) => c.product_id === drill.productId && c.bucket_start === drill.bucket,
  );
  const heading = t("planning.demand.consumption.title", {
    product: props.product?.sku ?? "",
    week: weekLabel(drill.bucket, i18n.language),
  });
  return (
    <Modal
      label={heading}
      heading={heading}
      closeLabel={t("planning.common.close")}
      onClose={props.onClose}
      className="planning-drawer"
      testId="planning-consumption"
    >
      {read.error !== null && !purged ? (
        <ErrorState
          title={t("planning.demand.consumption.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void read.refetch()}
          requestId={requestIdOf(read.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : read.isPending ? (
        <CardSkeleton lines={3} label={t("planning.demand.consumption.loading")} />
      ) : cell === undefined ? (
        <p className="planning-hint">{t("planning.demand.consumption.none")}</p>
      ) : (
        <div className="planning-drawer-section">
          <p data-testid="planning-consumption-summary">
            {t("planning.demand.consumption.summary", {
              original: withUnit(cell.qty_original),
              consumed: withUnit(cell.qty_consumed),
              net: withUnit(cell.qty_net),
            })}
          </p>
          {cell.consumers.length === 0 ? (
            <p className="planning-hint">{t("planning.demand.consumption.noOrders")}</p>
          ) : (
            <ul className="planning-line-list">
              {cell.consumers.map((c) => (
                <li
                  key={c.sales_order_line_id}
                  data-testid="planning-consumer"
                  data-line-id={c.sales_order_line_id}
                >
                  <OrderLabel label={c.order_label} />
                  <QtyWithUnit qty={c.qty_consumed} unit={props.unit} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
